import React, { useEffect, useState } from 'react';
import { followupsAPI, leadsAPI } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, Plus, CheckCircle, Bell, AlertCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FollowUps = () => {
  const [followups, setFollowups] = useState([]);
  const [dueFollowups, setDueFollowups] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    lead_id: '',
    scheduled_date: '',
    type: 'call',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [followupsRes, dueRes, leadsRes] = await Promise.all([
        followupsAPI.getAll(),
        axios.get(`${API}/followups/due`, { withCredentials: true }),
        leadsAPI.getAll()
      ]);
      setFollowups(followupsRes.data);
      setDueFollowups(dueRes.data);
      setLeads(leadsRes.data);
    } catch (error) {
      toast.error('Failed to load follow-ups');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        scheduled_date: new Date(formData.scheduled_date).toISOString()
      };
      await followupsAPI.create(submitData);
      toast.success('Follow-up scheduled successfully');
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Follow-up creation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to schedule follow-up');
    }
  };

  const handleComplete = async (followupId) => {
    try {
      await followupsAPI.complete(followupId);
      toast.success('Follow-up marked as complete');
      fetchData();
    } catch (error) {
      toast.error('Failed to complete follow-up');
    }
  };

  const handleSendReminder = async (followupId) => {
    try {
      const response = await axios.post(
        `${API}/followups/${followupId}/send-reminder`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to send reminder');
    }
  };

  const resetForm = () => {
    setFormData({
      lead_id: '',
      scheduled_date: '',
      type: 'call',
      notes: ''
    });
  };

  const getLeadName = (leadId) => {
    const lead = leads.find(l => l.lead_id === leadId);
    return lead ? `${lead.first_name} ${lead.last_name}` : 'Unknown Lead';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isOverdue = (dateString) => {
    return new Date(dateString) < new Date();
  };

  const upcomingFollowups = followups.filter(f => 
    !f.completed && new Date(f.scheduled_date) > new Date()
  );

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="followups-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-primary">Follow-up Manager</h1>
          <p className="text-muted-foreground mt-2">Schedule and track your lead follow-ups</p>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="schedule-followup-button">
              <Plus className="h-4 w-4" />
              Schedule Follow-up
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Schedule Follow-up</DialogTitle>
              <DialogDescription>Create a follow-up reminder for a lead</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="lead_id">Select Lead *</Label>
                <Select value={formData.lead_id} onValueChange={(value) => setFormData({ ...formData, lead_id: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a lead" />
                  </SelectTrigger>
                  <SelectContent>
                    {leads.map((lead) => (
                      <SelectItem key={lead.lead_id} value={lead.lead_id}>
                        {lead.first_name} {lead.last_name} - {lead.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="scheduled_date">Scheduled Date & Time *</Label>
                <Input
                  id="scheduled_date"
                  type="datetime-local"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="type">Follow-up Type *</Label>
                <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="call">Phone Call</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Add any notes about this follow-up"
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setShowDialog(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit">Schedule Follow-up</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Due & Overdue Follow-ups */}
      {dueFollowups.length > 0 && (
        <Card className="border-red-200 bg-red-50/50 dark:bg-red-950/20" data-testid="due-followups-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
              <AlertCircle className="h-5 w-5" />
              Action Required: {dueFollowups.length} Due/Overdue Follow-up{dueFollowups.length > 1 ? 's' : ''}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dueFollowups.map((followup, index) => (
                <Card key={followup.followup_id} className="bg-white dark:bg-zinc-900" data-testid={`due-followup-${index}`}>
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {isOverdue(followup.scheduled_date) ? (
                            <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400 font-medium">
                              OVERDUE
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-400 font-medium">
                              DUE TODAY
                            </span>
                          )}
                          <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400">
                            {(followup.type || followup.followup_type || 'Follow-up').toUpperCase()}
                          </span>
                        </div>
                        <p className="font-medium text-lg">
                          {followup.lead_info ? `${followup.lead_info.first_name} ${followup.lead_info.last_name}` : 'Unknown Lead'}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Scheduled: {formatDate(followup.scheduled_date)}
                        </p>
                        {followup.lead_info && (
                          <p className="text-sm text-muted-foreground mt-1">
                            Contact: {followup.lead_info.email} | {followup.lead_info.phone}
                          </p>
                        )}
                        {followup.notes && (
                          <p className="text-sm mt-2 text-muted-foreground italic">
                            Notes: {followup.notes}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSendReminder(followup.followup_id)}
                          className="gap-2"
                          data-testid={`send-reminder-${index}`}
                        >
                          <Bell className="h-4 w-4" />
                          Send Reminder
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleComplete(followup.followup_id)}
                          className="gap-2"
                          data-testid={`complete-${index}`}
                        >
                          <CheckCircle className="h-4 w-4" />
                          Complete
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upcoming Follow-ups */}
      <Card data-testid="upcoming-followups-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Upcoming Follow-ups ({upcomingFollowups.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {upcomingFollowups.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No upcoming follow-ups scheduled</p>
          ) : (
            <div className="space-y-3">
              {upcomingFollowups.map((followup, index) => (
                <Card key={followup.followup_id} data-testid={`upcoming-followup-${index}`}>
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400">
                            {(followup.type || followup.followup_type || 'Follow-up').toUpperCase()}
                          </span>
                        </div>
                        <p className="font-medium">{getLeadName(followup.lead_id)}</p>
                        <p className="text-sm text-muted-foreground">
                          Scheduled: {formatDate(followup.scheduled_date)}
                        </p>
                        {followup.notes && (
                          <p className="text-sm mt-2 text-muted-foreground italic">
                            Notes: {followup.notes}
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleComplete(followup.followup_id)}
                        className="gap-2"
                      >
                        <CheckCircle className="h-4 w-4" />
                        Complete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FollowUps;
